import React from 'react';
import { motion } from 'framer-motion';
import PageBanner from '../../components/ui/PageBanner';
import SectionHeading from '../../components/ui/SectionHeading';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import vieEtud from '../../assets/images/VieEtud.webp';
import etablissement from '../../assets/images/Etablissement.webp';
import journeesCulturelles from '../../assets/images/JourneesCulturelles.webp';
import feteFinAnnee from '../../assets/images/FeteFinAnnee.webp';
import EnseignementADistance from '../../assets/images/EnseignementADistance.webp';


import { MapPinIcon, ClockIcon, BookOpenIcon, AcademicCapIcon, UsersIcon } from '@heroicons/react/24/outline';

interface Activity {
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface SchoolEvent {
  title: string;
  date: string;
  description: string;
  image: string;
}

const activities: Activity[] = [
  {
    title: 'Clubs artistiques',
    description: "Théâtre, musique, arts plastiques, photographie, et bien plus pour développer la créativité et l'expression personnelle.",
    icon: AcademicCapIcon,
  },
  {
    title: 'Clubs scientifiques',
    description: "Robotique, informatique, sciences expérimentales, mathématiques pour encourager l'innovation et la curiosité scientifique.",
    icon: BookOpenIcon,
  },
  {
    title: 'Activités sportives',
    description: "Football, basketball, volleyball, athlétisme, natation et autres sports pour promouvoir un mode de vie sain et actif.",
    icon: UsersIcon,
  },
  {
    title: 'Sorties éducatives',
    description: "Visites de musées, sites historiques, institutions publiques et sorties culturelles pour enrichir les connaissances des élèves.",
    icon: MapPinIcon,
  },
];

const events: SchoolEvent[] = [
  {
    title: 'Journée portes ouvertes',
    date: 'Septembre',
    description: "Venez découvrir notre établissement, rencontrer notre équipe pédagogique et en apprendre plus sur nos programmes.",
    image: etablissement
  },
  {
    title: 'Fête de fin d\'année',
    date: 'Juin',
    description: "Célébration des accomplissements de l'année scolaire avec des spectacles, expositions et remises de prix.",
    image: feteFinAnnee,
  },
  {
    title: 'Journées culturelles',
    date: 'Mars',
    description: "Événements thématiques célébrant la diversité culturelle à travers des activités, expositions et performances.",
    image: journeesCulturelles,
  },
];

const StudentLifePage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageBanner
        title="Vie Étudiante"
        subtitle="L'épanouissement au-delà des salles de classe"
        imageSrc={vieEtud}
      />

      {/* Telegram Section */}
      <section className="py-16 bg-white">
  <div className="container mx-auto px-4 text-center">
    <SectionHeading
      title="📲 Rejoignez-nous sur Telegram"
      subtitle="Restez connectés et informés en temps réel !"
      centered={true}
    />
    <div className="max-w-3xl mx-auto">
      <p className="text-lg text-gray-700 mb-8">
        Grâce à notre service sur Telegram, vous pouvez suivre facilement :
      </p>
      <ul className="text-left space-y-4 mb-8 max-w-xl mx-auto">
        <li className="flex items-start">
          <span className="mr-2">🚸</span>
          <p className="text-gray-700">Le transport scolaire : recevez une alerte lorsque le bus approche de votre enfant.</p>
        </li>
        <li className="flex items-start">
          <span className="mr-2">📆</span>
          <p className="text-gray-700">L'emploi du temps et les horaires scolaires.</p>
        </li>
        <li className="flex items-start">
          <span className="mr-2">📚</span>
          <p className="text-gray-700">Le compte Google Classroom de votre enfant.</p>
        </li>
        <li className="flex items-start">
          <span className="mr-2">⚠️</span>
          <p className="text-gray-700">Les absences, remarques scolaires et règlements.</p>
        </li>
      </ul>
      <p className="text-lg text-gray-700 font-medium mb-8">
        🤖 Nouveau : Parler à un agent propulsé par l'intelligence artificielle pour répondre aux questions sur la scolarité de vos enfants.
      </p>
      <p className="text-lg text-gray-700 font-medium mb-8">
        👉 Rejoignez-nous dès maintenant pour un suivi simple, direct et efficace de la vie scolaire de votre enfant.
      </p>
      <div className="flex flex-col space-y-4 mt-8">
              <a 
                href="https://t.me/gssanaa3_bot" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-lg text-center text-lg transition duration-300"
              >
                Telegram Maternelle Primaire
              </a>
              <a 
                href="https://t.me/gssanaa2_bot" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-lg text-center text-lg transition duration-300"
              >
                Telegram Collège Lycée
              </a>
    </div>
    </div>
  </div>
</section>

      {/* Introduction Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="La vie à Groupe Scolaire Sanaa"
            subtitle="Une expérience enrichissante"
            centered={true}
          />
          <div className="max-w-3xl mx-auto mt-8 text-center">
            <p className="text-lg text-gray-700 mb-8">
              Au Groupe Scolaire Sanaa, nous croyons que l'éducation va bien au-delà des salles de classe. Notre établissement offre un environnement riche et stimulant où les élèves peuvent s'épanouir à travers diverses activités parascolaires, événements et projets éducatifs.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Nous encourageons nos élèves à explorer leurs passions, développer leurs talents et s'engager dans la communauté scolaire. C'est à travers ces expériences qu'ils développent des compétences essentielles pour leur réussite future : leadership, travail d'équipe, créativité et responsabilité.
            </p>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Activités parascolaires"
            subtitle="Développer les talents"
            centered={true}
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {activities.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full p-6">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <activity.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{activity.title}</h3>
                  <p className="text-gray-600">{activity.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Distance Learning Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <SectionHeading
                title="Enseignement à distance"
                subtitle="Continuité pédagogique"
              />
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Le Groupe Scolaire Sanaa dispose d'une plateforme d'enseignement à distance performante, permettant d'assurer la continuité pédagogique en toutes circonstances. Nos élèves bénéficient d'un accès à des ressources éducatives numériques variées et à des cours interactifs en ligne.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Nos enseignants sont formés aux méthodes pédagogiques adaptées à l'enseignement à distance, utilisant des outils comme Google Meet et Google Classroom pour les classes virtuelles et le suivi des devoirs.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <ClockIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Sessions en direct avec les enseignants</p>
                </li>
                <li className="flex items-start">
                  <BookOpenIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Ressources éducatives numériques variées</p>
                </li>
                <li className="flex items-start">
                  <AcademicCapIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Suivi personnalisé des progrès de chaque élève</p>
                </li>
              </ul>
            </motion.div>
            {
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary rounded-tr-3xl z-0"></div>
                <img 
                  src={EnseignementADistance}
                  alt="Enseignement à distance au Groupe Scolaire Sanaa" 
                  className="rounded-lg shadow-elegant w-full h-full object-cover relative z-10"
                />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary rounded-bl-3xl z-0"></div>
              </div>
            </motion.div>
            }
          </div>
        </div>
      </section>

      {/* School Events Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Événements scolaires"
            subtitle="Moments forts de l'année"
            centered={true}
          />
          
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {events.map((event, index) => (
              <motion.div
                key={event.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full overflow-hidden">
                  <div className="h-48 relative">
                    <img 
                      src={event.image} 
                      alt={event.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0 right-0 bg-primary text-white px-4 py-2 font-medium">
                      {event.date}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{event.title}</h3>
                    <p className="text-gray-600">{event.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Rejoignez notre communauté dynamique</h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Offrez à votre enfant une expérience éducative complète qui lui permettra de s'épanouir pleinement.
          </p>
          <Button to="/contact" variant="outline-white" size="large">
            Nous contacter
          </Button>
        </div>
      </section>
    </motion.div>
  );
};

export default StudentLifePage;
